import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin, Star, Globe, BookMarked, Building } from "lucide-react";

// Génère un identifiant de session Google Places (facturation « session » : autocomplete + details
// facturés comme UNE session à ~0,003 $). Doit être RÉGÉNÉRÉ après chaque sélection.
function newSessionToken() {
  return "pcm-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Champ d'autocomplétion d'adresses avec priorité :
//  1) lieux de mon annuaire privé (avec compteur d'utilisation)
//  2) lieux publics contribués par d'autres gérants
//  3) résultats Google Places (précision optimale FR — repli OSM/Nominatim automatique côté backend)
export function AddressAutocomplete({ value, onChange, onSelect, placeholder, testid, excludeType }) {
  const [places, setPlaces] = useState([]);
  const [geo, setGeo] = useState([]);
  const [geoSource, setGeoSource] = useState("google");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyPick, setBusyPick] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);
  const sessionToken = useRef(newSessionToken());

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handle = (txt) => {
    onChange?.(txt);
    if (timer.current) clearTimeout(timer.current);
    if (!txt || txt.trim().length < 2) { setPlaces([]); setGeo([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      const q = encodeURIComponent(txt.trim());
      try {
        const placesP = api.get(`/places/search?q=${q}&limit=6${excludeType ? `&exclude_type=${encodeURIComponent(excludeType)}` : ""}`).then(r => r.data.results || []).catch(() => []);
        const geoP = txt.trim().length >= 3
          ? api.get(`/geo/search?q=${q}&session_token=${sessionToken.current}`).then(r => ({ results: r.data.results || [], source: r.data.source || "osm" })).catch(() => ({ results: [], source: "none" }))
          : Promise.resolve({ results: [], source: "none" });
        const [pl, gr] = await Promise.all([placesP, geoP]);
        setPlaces(pl);
        const seen = new Set(pl.map(p => (p.address || p.label || "").toLowerCase()));
        setGeo((gr.results || []).filter(g => !seen.has((g.label || "").toLowerCase())).slice(0, 5));
        setGeoSource(gr.source);
        setOpen(true);
      } catch { setPlaces([]); setGeo([]); }
      finally { setLoading(false); }
    }, 300);
  };

  const pickPlace = (p) => {
    onSelect?.({ label: p.address || p.label, address: p.address || p.label, lat: p.lat, lon: p.lon, place: p });
    setOpen(false); setPlaces([]); setGeo([]);
    sessionToken.current = newSessionToken();
  };
  const pickGeo = async (r) => {
    // Google : nécessite un appel Place Details pour obtenir lat/lon
    if (r.source === "google" && r.place_id) {
      setBusyPick(true);
      try {
        const { data } = await api.get(`/geo/place/${encodeURIComponent(r.place_id)}?session_token=${sessionToken.current}`);
        onSelect?.({ label: data.label || r.label, address: data.label || r.label, lat: data.lat, lon: data.lon });
      } catch {
        onSelect?.({ label: r.label, address: r.label, lat: null, lon: null });
      } finally { setBusyPick(false); }
    } else {
      // OSM : lat/lon déjà présents dans le résultat
      onSelect?.({ label: r.label, address: r.label, lat: r.lat, lon: r.lon });
    }
    setOpen(false); setPlaces([]); setGeo([]);
    sessionToken.current = newSessionToken();
  };

  const hasAny = places.length > 0 || geo.length > 0;
  const geoLabel = geoSource === "google" ? "Google Maps" : "OpenStreetMap (repli)";
  const GeoIcon = geoSource === "google" ? Building : Globe;

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Input value={value || ""} onChange={(e) => handle(e.target.value)} placeholder={placeholder} data-testid={testid} autoComplete="off" />
        {(loading || busyPick) && <Loader2 className="h-4 w-4 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" />}
      </div>
      {open && hasAny && (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-72 overflow-auto" data-testid={`${testid}-suggestions`}>
          {places.length > 0 && (
            <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/30 border-b border-border">Mon annuaire &amp; base commune</div>
          )}
          {places.map((p, i) => (
            <button key={`p-${i}`} type="button" onClick={() => pickPlace(p)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-start gap-2" data-testid={`${testid}-place-${i}`}>
              {p.mine ? <BookMarked className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" /> : <Star className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />}
              <span className="flex-1 min-w-0">
                <span className="font-medium truncate block">{p.label}</span>
                <span className="text-[11px] text-muted-foreground truncate block">
                  {p.place_type || "Lieu"}{p.specialite ? ` · ${p.specialite}` : ""}{p.floor ? ` · ${p.floor}` : ""}{p.phone ? ` · ${p.phone}` : ""}
                  {!p.mine && " · base commune"}
                </span>
              </span>
            </button>
          ))}
          {geo.length > 0 && (
            <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/30 border-b border-t border-border">Adresses {geoLabel}</div>
          )}
          {geo.map((r, i) => (
            <button key={`g-${i}`} type="button" onClick={() => pickGeo(r)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-start gap-2" data-testid={`${testid}-geo-${i}`}>
              <GeoIcon className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${r.source === "google" ? "text-primary" : "text-muted-foreground"}`} />
              <span className="truncate">{r.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
